import React, { useState, useRef, useMemo } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Stack,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  Chip,
  Grid,
} from "@mui/material"
import {
  Close as CloseIcon,
  Print as PrintIcon,
  FileDownload as DownloadIcon,
  CalendarToday as CalendarIcon,
} from "@mui/icons-material"
import { useReactToPrint } from "react-to-print"
import * as XLSX from "xlsx"
import { useApiQuery } from "../../hooks/useApi"
import { useQueryClient } from "@tanstack/react-query"

function ReportGenerator({ open, onClose }) {
  const [filterType, setFilterType] = useState("month")
  const [startDate, setStartDate] = useState("")
  const [endDate, setEndDate] = useState("")
  // const [reportData, setReportData] = useState(null)
  // const [loading, setLoading] = useState(false)
  // const [error, setError] = useState(null)
  const reportRef = useRef()

  // compute URL dynamically
  const reportUrl = useMemo(() => {
    if (filterType === "custom" && startDate && endDate) {
      return `/receipts/generate/report?startDate=${startDate}&endDate=${endDate}`
    }
    return `/receipts/generate/report?filter=${filterType}`
  }, [filterType, startDate, endDate])

  const queryClient = useQueryClient();

  // useApiQuery integration
  const {
    data: reportData,
    error,
    isFetching,
    refetch,
  } = useApiQuery(["report", reportUrl], reportUrl, {
    enabled: false, // don’t auto-fetch; fetch manually
  })

  const handleGenerateReport = () => {
    refetch()
  }

  const handlePrint = useReactToPrint({
    contentRef: reportRef,
    documentTitle: `Fee_Collection_Report_${new Date().toISOString().split("T")[0]}`,
  })

  const handleDownloadExcel = () => {
    if (!reportData) return

    const workbook = XLSX.utils.book_new()

    // Summary Sheet
    const summaryData = [
      ["Fee Collection Report"],
      [""],
      ["Report Period", `${new Date(reportData.summary.dateRange.start).toLocaleDateString()} - ${new Date(reportData.summary.dateRange.end).toLocaleDateString()}`],
      ["Total Amount Collected", `₹${reportData.summary.totalAmount.toLocaleString()}`],
      ["Total Receipts", reportData.summary.receiptCount],
      [""],
      ["Payment Method Breakdown"],
      ["Method", "Count", "Total Amount"],
    ]

    Object.entries(reportData.summary.byPaymentMethod).forEach(([method, data]) => {
      summaryData.push([method.toUpperCase(), data.count, data.total]);
    });

    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData)
    XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary")

    // Receipts Sheet
    const receiptsData = [
      [
        "Receipt No.",
        "Date",
        "Client Name",
        "Client Phone",
        "Job ID",
        "Job Type",
        "Amount",
        "Payment Method",
        "Bank", 
        "Transaction Ref",
        "Collected By",
        "Job Status",
        "Notes",
      ],
    ]

    reportData.receipts.forEach((receipt) => {
      receiptsData.push([
        receipt.receiptNumber,
        new Date(receipt.date).toLocaleString(),
        receipt.clientName,
        receipt.clientPhone,
        receipt.jobId,
        receipt.jobType,
        receipt.amount,
        receipt.paymentMethod,
        receipt.paymentMethod === "bank_transfer" ? receipt.bank : "N/A",
        receipt.transactionReference,
        receipt.collectedBy,
        receipt.jobStatus,
        receipt.notes,
      ])
    })

    const receiptsSheet = XLSX.utils.aoa_to_sheet(receiptsData)
    XLSX.utils.book_append_sheet(workbook, receiptsSheet, "Receipts")

    // Download
    XLSX.writeFile(workbook, `Fee_Collection_Report_${new Date().toISOString().split("T")[0]}.xlsx`)
  }

  const handleDownloadPDF = () => {
    handlePrint()
  }

  const getFilterLabel = () => {
    switch (filterType) {
      case "today":
        return "Today"
      case "30days":
        return "Last 30 Days"
      case "month":
        return "This Month"
      case "all":
        return "All Time"
      case "custom":
        return "Custom Range"
      default:
        return "This Month"
    }
  }

  const handleClose = () => {
    queryClient.removeQueries(["report", reportUrl]); 
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" fontWeight="bold">
            Generate Fee Collection Report
          </Typography>
          <Button onClick={handleClose} color="inherit">
            <CloseIcon />
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Filter Selection */}
        {!reportData && (
          <Box sx={{ mb: 3 }}>
            <Stack spacing={3}>
              <FormControl fullWidth>
                <InputLabel>Report Period</InputLabel>
                <Select
                  value={filterType}
                  label="Report Period"
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="30days">Last 30 Days</MenuItem>
                  <MenuItem value="month">This Month</MenuItem>
                  <MenuItem value="all">All Time</MenuItem>
                  <MenuItem value="custom">Custom Date Range</MenuItem>
                </Select>
              </FormControl>

              {filterType === "custom" && (
                <Stack direction="row" spacing={2}>
                  <TextField
                    type="date"
                    label="Start Date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                  <TextField
                    type="date"
                    label="End Date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Stack>
              )}

              {error && (
                <Alert severity="error" >
                  {error.message}
                </Alert>
              )}

              <Button
                variant="contained"
                size="large"
                onClick={handleGenerateReport}
                disabled={isFetching || (filterType === "custom" && (!startDate || !endDate))}
                startIcon={isFetching ? <CircularProgress size={20} /> : <CalendarIcon />}
              >
                {isFetching ? "Generating..." : "Generate Report"}
              </Button>
            </Stack>
          </Box>
        )}

        {/* Report Display */}
        {reportData && (
          <Box>
            {/* Action Buttons */}
            <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
              <Button variant="outlined" startIcon={<PrintIcon />} onClick={handleDownloadPDF}>
                Print / Save as PDF
              </Button>
              <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadExcel}>
                Download Excel
              </Button>
              {/* <Button variant="text" onClick={() => setReportData(null)}>
                New Report
              </Button> */}
            </Stack>

            {/* Printable Report */}
            <Box ref={reportRef} sx={{ p: 3, bgcolor: "white" }}>
              {/* Report Header */}
              <Box sx={{ textAlign: "center", mb: 4 }}>
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                  Fee Collection Report
                </Typography>
                <Typography variant="subtitle1" color="textSecondary">
                  {new Date(reportData.summary.dateRange.start).toLocaleDateString()} -{" "}
                  {new Date(reportData.summary.dateRange.end).toLocaleDateString()}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Generated on: {new Date().toLocaleString()}
                </Typography>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Summary Statistics */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Summary
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Paper sx={{ p: 2, bgcolor: "success.light" }}>
                      <Typography variant="body2" color="textSecondary">
                        Total Amount Collected
                      </Typography>
                      <Typography variant="h4" fontWeight="bold">
                        ₹{reportData.summary.totalAmount.toLocaleString()}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Paper sx={{ p: 2, bgcolor: "primary.light" }}>
                      <Typography variant="body2" color="textSecondary">
                        Total Receipts
                      </Typography>
                      <Typography variant="h4" fontWeight="bold">
                        {reportData.summary.receiptCount}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>

              {/* Payment Method Breakdown */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Collection by Payment Method
                </Typography>

                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <strong>Payment Method</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Count</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Total Amount</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {Object.entries(reportData.summary.byPaymentMethod).map(([method, data]) => (
                        <React.Fragment key={method}>

                          {/* MAIN PAYMENT METHOD ROW */}
                          <TableRow>
                            <TableCell>
                              <Chip label={method.toUpperCase()} size="small" />
                            </TableCell>
                            <TableCell align="right">{data.count}</TableCell>
                            <TableCell align="right">₹{data.total.toLocaleString()}</TableCell>
                          </TableRow>

                          {/* BANK BREAKDOWN (ONLY FOR bank_transfer) */}
                          {method === "bank_transfer" &&
                            reportData.receipts
                              .filter(r => r.paymentMethod === "bank_transfer")
                              .reduce((acc, r) => {
                                acc[r.bank] = acc[r.bank] || { count: 0, total: 0 }
                                acc[r.bank].count += 1
                                acc[r.bank].total += r.amount || 0
                                return acc
                              }, {})
                            &&
                            Object.entries(
                              reportData.receipts
                                .filter(r => r.paymentMethod === "bank_transfer")
                                .reduce((acc, r) => {
                                  acc[r.bank] = acc[r.bank] || { count: 0, total: 0 }
                                  acc[r.bank].count += 1
                                  acc[r.bank].total += r.amount || 0
                                  return acc
                                }, {})
                            ).map(([bankName, info]) => (
                              <TableRow key={bankName}>
                                <TableCell sx={{ pl: 4 }}>
                                  {bankName}
                                </TableCell>
                                <TableCell align="right">{info.count}</TableCell>
                                <TableCell align="right">₹{info.total.toLocaleString()}</TableCell>
                              </TableRow>
                            ))
                          }

                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>


              {/* Detailed Receipts */}
              <Box>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Detailed Receipts
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <strong>Receipt No.</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Date</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Client</strong>
                        </TableCell>
                        {/* <TableCell>
                          <strong>Job Type</strong>
                        </TableCell> */}
                        <TableCell align="right">
                          <strong>Amount</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Payment Method</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Bank</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Collected By</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reportData.receipts.map((receipt) => (
                        <TableRow key={receipt.receiptNumber}>
                          <TableCell sx={{ fontFamily: "monospace" }}>{receipt.receiptNumber}</TableCell>
                          <TableCell>{new Date(receipt.date).toLocaleDateString()}</TableCell>
                          <TableCell>
                            {receipt.clientName}
                            <Typography variant="caption" display="block" color="textSecondary">
                              {receipt.clientPhone}
                            </Typography>
                          </TableCell>
                          {/* <TableCell>{receipt.jobType}</TableCell> */}
                          <TableCell align="right" sx={{ fontWeight: "bold" }}>
                            ₹{receipt.amount.toLocaleString()}
                          </TableCell>
                          <TableCell>
                            <Chip label={receipt.paymentMethod.toUpperCase()} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell>
                            {receipt?.bank || "N/A"}
                          </TableCell>
                          <TableCell>{receipt.collectedBy}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {/* Footer */}
              <Box sx={{ mt: 4, pt: 2, borderTop: "1px solid #ddd", textAlign: "center" }}>
                <Typography variant="caption" color="textSecondary">
                  This is a computer-generated report. For any queries, please contact the administration.
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

export default ReportGenerator